import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Users, Pin, PinOff, Gavel, ListTodo, Paperclip,
  Trash2, Send, X, Link2, Check, ChevronDown,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const POLL_MS = 8000;

export default function ChatTab({ eventId, members, canEdit, myRole, myMemberId }) {
  const isPlanner = myRole === "owner" || myRole === "coordinator";
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [body, setBody] = useState("");
  const listEndRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/threads`);
      setThreads(data);
      if (!activeId && data.length > 0) setActiveId(data[0].id);
    } catch (e) {
      toast.error("Could not load threads");
    } finally {
      setLoading(false);
    }
  }, [eventId, activeId]);

  const loadMessages = useCallback(async () => {
    if (!activeId) { setMessages([]); return; }
    try {
      const { data } = await api.get(`/threads/${activeId}/messages`);
      setMessages(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setActiveId(null); setMessages([]);
      }
    }
  }, [activeId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Poll both threads (for unread counts + new threads) and messages every 8s.
  useEffect(() => {
    const t = setInterval(() => { loadThreads(); loadMessages(); }, POLL_MS);
    return () => clearInterval(t);
  }, [loadThreads, loadMessages]);

  // Auto-scroll on new message
  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const activeThread = useMemo(() => threads.find((t) => t.id === activeId), [threads, activeId]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    if (!body.trim() || !activeId) return;
    const draft = body.trim();
    setBody("");
    try {
      await api.post(`/threads/${activeId}/messages`, { body: draft });
      await loadMessages();
      await loadThreads();
    } catch (err) {
      toast.error("Could not send");
      setBody(draft);
    }
  };

  const patchMessage = async (mid, changes) => {
    try {
      await api.patch(`/messages/${mid}`, changes);
      await loadMessages();
    } catch { toast.error("Update failed"); }
  };

  const deleteMessage = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/messages/${mid}`); await loadMessages(); }
    catch { toast.error("Delete failed"); }
  };

  const convertToTask = async (mid) => {
    try {
      const { data } = await api.post(`/messages/${mid}/convert-to-task`, {});
      toast.success(`Task created: ${data.task.title}`);
      await loadMessages();
    } catch { toast.error("Could not convert"); }
  };

  const pinnedMsgs = useMemo(() => messages.filter((m) => m.pinned), [messages]);
  const decisions = useMemo(() => messages.filter((m) => m.is_decision), [messages]);

  return (
    <div className="p-6 md:p-10">
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 min-h-[600px]">
        {/* THREAD RAIL */}
        <aside className="bevel border-hair flex flex-col">
          <header className="px-4 py-3 border-b border-hair flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h3 className="font-display text-sm font-bold text-app uppercase tracking-tight">Threads</h3>
              <span className="font-mono text-[10px] tabular-nums text-muted">[{String(threads.length).padStart(2, "0")}]</span>
            </div>
            {isPlanner && (
              <button
                onClick={() => setNewThreadOpen(true)}
                data-testid="chat-new-thread-btn"
                title="New thread"
                className="p-1 text-app hover:text-[#F59E0B]"
              >
                <Plus size={14} strokeWidth={2} />
              </button>
            )}
          </header>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 font-mono text-xs text-muted">LOADING…</div>
            ) : threads.length === 0 ? (
              <div className="p-6 text-center">
                <MessageSquare size={20} strokeWidth={1.5} className="mx-auto text-faint mb-2" />
                <p className="text-xs text-muted">
                  {isPlanner ? "Start a thread with a vendor or client." : "No threads yet."}
                </p>
              </div>
            ) : threads.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveId(t.id)}
                data-testid={`thread-item-${t.id}`}
                className={`w-full text-left px-4 py-3 border-b border-hair hover:bg-inset ${
                  activeId === t.id ? "bg-surface-2" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-sm font-semibold text-app truncate">{t.name}</div>
                    {t.last_message_preview && (
                      <div className="text-xs text-muted truncate mt-0.5">{t.last_message_preview}</div>
                    )}
                  </div>
                  {t.unread > 0 && (
                    <span className="font-mono text-[10px] font-bold bg-[#EF4444] text-white px-1.5 py-0.5 tabular-nums flex-shrink-0">
                      {t.unread}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* MESSAGE PANE */}
        <section className="bevel border-hair flex flex-col min-h-[600px]" data-testid="chat-message-pane">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-muted">
              <div className="text-center">
                <MessageSquare size={28} strokeWidth={1.5} className="mx-auto text-faint mb-3" />
                <div className="text-sm">Select a thread to start</div>
              </div>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <header className="px-5 py-3 border-b border-hair flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted mb-1">
                    THR.{activeThread.id.slice(-6).toUpperCase()} · {activeThread.kind.toUpperCase()}
                  </div>
                  <div className="font-display text-lg font-bold text-app truncate">{activeThread.name}</div>
                  <ParticipantsList thread={activeThread} members={members} />
                </div>
                {isPlanner && (
                  <button
                    onClick={() => setManageOpen(true)}
                    data-testid="thread-manage-btn"
                    className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-hair hover:border-[#0A0A0A]"
                  >
                    Manage
                  </button>
                )}
              </header>

              {/* Pinned / Decisions banners */}
              {(pinnedMsgs.length > 0 || decisions.length > 0) && (
                <div className="px-5 py-2 border-b border-hair bg-inset flex items-center gap-4 flex-wrap text-xs">
                  {pinnedMsgs.length > 0 && (
                    <div className="flex items-center gap-1.5 text-[#F59E0B]" data-testid="pinned-summary">
                      <Pin size={12} strokeWidth={2} /><span className="font-mono uppercase tracking-wider">{pinnedMsgs.length} pinned</span>
                    </div>
                  )}
                  {decisions.length > 0 && (
                    <div className="flex items-center gap-1.5 text-[#059669]" data-testid="decisions-summary">
                      <Gavel size={12} strokeWidth={2} /><span className="font-mono uppercase tracking-wider">{decisions.length} decisions</span>
                    </div>
                  )}
                </div>
              )}

              {/* Message list */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages-list">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-muted py-12">No messages yet. Say hi.</div>
                ) : messages.map((m) => (
                  <MessageRow
                    key={m.id}
                    msg={m}
                    isPlanner={isPlanner}
                    onPatch={patchMessage}
                    onDelete={deleteMessage}
                    onConvertToTask={convertToTask}
                    members={members}
                  />
                ))}
                <div ref={listEndRef} />
              </div>

              {/* Composer */}
              <form onSubmit={sendMessage} className="border-t border-hair p-3 flex items-end gap-2" data-testid="chat-composer">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Type a message… (Shift+Enter for newline)"
                  rows={2}
                  data-testid="chat-composer-input"
                  className="flex-1 resize-none border border-hair rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]"
                />
                <Button type="submit" data-testid="chat-send-btn" className="rounded-sm bg-app hover:bg-black h-10">
                  <Send size={14} strokeWidth={2} />
                </Button>
              </form>
            </>
          )}
        </section>
      </div>

      {/* Dialogs */}
      <NewThreadDialog
        open={newThreadOpen}
        onOpenChange={setNewThreadOpen}
        eventId={eventId}
        members={members}
        onCreated={(t) => { setActiveId(t.id); loadThreads(); }}
      />
      {activeThread && (
        <ManageThreadDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          thread={activeThread}
          members={members}
          onChanged={loadThreads}
          onDeleted={() => { setActiveId(null); loadThreads(); }}
        />
      )}
    </div>
  );
}

function ParticipantsList({ thread, members }) {
  const names = (thread.participant_member_ids || [])
    .map((id) => members.find((m) => m.id === id)?.name || "Unknown")
    .slice(0, 4);
  const extra = (thread.participant_member_ids || []).length - names.length;
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted mt-1">
      <Users size={11} strokeWidth={1.75} className="text-muted" />
      <span className="truncate">Planner{names.length > 0 && ` · ${names.join(", ")}`}{extra > 0 && ` +${extra}`}</span>
    </div>
  );
}

function MessageRow({ msg, isPlanner, onPatch, onDelete, onConvertToTask }) {
  const time = new Date(msg.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return (
    <div
      data-testid={`msg-${msg.id}`}
      className={`group relative border-l-2 pl-3 pr-2 py-2 ${
        msg.is_decision ? "border-[#059669] bg-emerald-50/30"
        : msg.pinned ? "border-[#F59E0B] bg-amber-50/30"
        : "border-transparent hover:border-hair hover:bg-inset"
      }`}
    >
      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-display text-sm font-semibold text-app">{msg.author_name || "Someone"}</span>
        <span className="font-mono text-[10px] text-faint tabular-nums">{time}</span>
        {msg.pinned && <span className="font-mono text-[9px] tracking-wider uppercase text-[#F59E0B] flex items-center gap-1"><Pin size={9} strokeWidth={2} />PINNED</span>}
        {msg.is_decision && <span className="font-mono text-[9px] tracking-wider uppercase text-[#059669] flex items-center gap-1"><Gavel size={9} strokeWidth={2} />DECISION</span>}
      </div>
      <div className="text-sm text-app whitespace-pre-wrap break-words">{msg.body}</div>

      {msg.links?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {msg.links.map((l, i) => (
            <span key={`${l.kind}-${l.id ?? i}`} data-testid={`link-chip-${l.kind}`} className="inline-flex items-center gap-1 font-mono text-[10px] tracking-wider uppercase px-1.5 py-0.5 bg-surface-2 text-app">
              <Link2 size={10} strokeWidth={2} />{l.kind}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 bevel border-hair shadow-sm">
        {isPlanner && (
          <>
            <button title={msg.pinned ? "Unpin" : "Pin"} onClick={() => onPatch(msg.id, { pinned: !msg.pinned })}
              data-testid={`msg-action-pin-${msg.id}`}
              className="p-1.5 hover:bg-inset text-muted hover:text-[#F59E0B]">
              {msg.pinned ? <PinOff size={12} strokeWidth={2} /> : <Pin size={12} strokeWidth={2} />}
            </button>
            <button title={msg.is_decision ? "Unmark decision" : "Mark as decision"} onClick={() => onPatch(msg.id, { is_decision: !msg.is_decision })}
              data-testid={`msg-action-decision-${msg.id}`}
              className="p-1.5 hover:bg-inset text-muted hover:text-[#059669]">
              <Gavel size={12} strokeWidth={2} />
            </button>
            <button title="Convert to task" onClick={() => onConvertToTask(msg.id)}
              data-testid={`msg-action-task-${msg.id}`}
              className="p-1.5 hover:bg-inset text-muted hover:text-app">
              <ListTodo size={12} strokeWidth={2} />
            </button>
          </>
        )}
        <button title="Delete" onClick={() => onDelete(msg.id)}
          data-testid={`msg-action-delete-${msg.id}`}
          className="p-1.5 hover:bg-inset text-muted hover:text-[#EF4444]">
          <Trash2 size={12} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

function NewThreadDialog({ open, onOpenChange, eventId, members, onCreated }) {
  const [name, setName] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const toggleMember = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const submit = async () => {
    if (!name.trim()) { toast.error("Give the thread a name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/events/${eventId}/threads`, {
        name: name.trim(),
        kind: selectedIds.length === 1 ? "direct" : "group",
        participant_member_ids: selectedIds,
      });
      toast.success("Thread created");
      onCreated?.(data);
      onOpenChange(false);
      setName(""); setSelectedIds([]);
    } catch { toast.error("Could not create"); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bevel-lg">
        <DialogHeader><DialogTitle className="font-serif text-xl">New thread</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="eyebrow block mb-2">Thread name</Label>
            <Input data-testid="new-thread-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Planner + Photographer" className="rounded-sm" />
          </div>
          <div>
            <Label className="eyebrow block mb-2">Participants ({selectedIds.length})</Label>
            <div className="border border-hair rounded-sm max-h-56 overflow-y-auto">
              {members.length === 0 && <div className="p-3 text-xs text-muted">No members yet — invite vendors/clients from the Vendors tab.</div>}
              {members.map((m) => (
                <button key={m.id} type="button" onClick={() => toggleMember(m.id)}
                  data-testid={`new-thread-member-${m.id}`}
                  className={`w-full text-left px-3 py-2 border-b border-hair last:border-b-0 flex items-center gap-2 text-sm hover:bg-inset ${selectedIds.includes(m.id) ? "bg-surface-2" : ""}`}>
                  <span className={`w-4 h-4 border ${selectedIds.includes(m.id) ? "bg-app border-[#0A0A0A]" : "border-hair-emph"} flex items-center justify-center`}>
                    {selectedIds.includes(m.id) && <Check size={10} strokeWidth={3} className="text-white" />}
                  </span>
                  <span className="flex-1 min-w-0">{m.name}</span>
                  <span className="font-mono text-[10px] uppercase text-muted">{m.role}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-thread-submit" className="rounded-sm bg-app hover:bg-black">
            Create thread
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageThreadDialog({ open, onOpenChange, thread, members, onChanged, onDeleted }) {
  const [name, setName] = useState(thread.name);
  useEffect(() => { setName(thread.name); }, [thread]);
  const currentIds = thread.participant_member_ids || [];
  const rename = async () => {
    if (name.trim() === thread.name) return;
    try { await api.patch(`/threads/${thread.id}`, { name: name.trim() }); toast.success("Renamed"); onChanged(); }
    catch { toast.error("Rename failed"); }
  };
  const addMember = async (memberId) => {
    try { await api.post(`/threads/${thread.id}/participants`, { member_id: memberId }); onChanged(); }
    catch { toast.error("Add failed"); }
  };
  const removeMember = async (memberId) => {
    try { await api.delete(`/threads/${thread.id}/participants/${memberId}`); onChanged(); }
    catch { toast.error("Remove failed"); }
  };
  const deleteThread = async () => {
    if (!window.confirm(`Delete "${thread.name}"? All messages will be lost.`)) return;
    try { await api.delete(`/threads/${thread.id}`); toast.success("Deleted"); onDeleted(); onOpenChange(false); }
    catch { toast.error("Delete failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bevel-lg">
        <DialogHeader><DialogTitle className="font-serif text-xl">Manage thread</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="eyebrow block mb-2">Name</Label>
            <div className="flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="manage-thread-name" />
              <Button onClick={rename} className="rounded-sm bg-app hover:bg-black" data-testid="manage-thread-rename">Rename</Button>
            </div>
          </div>
          <div>
            <Label className="eyebrow block mb-2">Participants</Label>
            <div className="border border-hair rounded-sm max-h-56 overflow-y-auto">
              {members.map((m) => {
                const isIn = currentIds.includes(m.id);
                return (
                  <div key={m.id} className="px-3 py-2 border-b border-hair last:border-b-0 flex items-center gap-2 text-sm">
                    <span className="flex-1 min-w-0 truncate">{m.name}</span>
                    <span className="font-mono text-[10px] uppercase text-muted">{m.role}</span>
                    <button
                      onClick={() => isIn ? removeMember(m.id) : addMember(m.id)}
                      data-testid={`manage-thread-toggle-${m.id}`}
                      className={`text-xs px-2 py-1 border ${isIn ? "border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white" : "border-hair hover:border-[#0A0A0A]"}`}
                    >
                      {isIn ? "Remove" : "Add"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={deleteThread} data-testid="manage-thread-delete" className="text-[#EF4444] hover:bg-[#FEF2F2]">
            <Trash2 size={13} strokeWidth={1.75} className="mr-1.5" /> Delete thread
          </Button>
          <Button onClick={() => onOpenChange(false)} className="rounded-sm bg-app hover:bg-black">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
